/**
 * Credit limit and hold (piece 10). 🔨 The credit limit only ever warns, carried
 * into the approval the document already needs; it never blocks by itself. 🔨
 * Only a person sets Hold, and nothing ever auto-holds an account.
 */
import { formatCurrency, roundHalfUpCents, type Currency } from '../money';
import { fill, t } from '../strings';

export const CREDIT_STATUSES = ['Good', 'Watch', 'Hold'] as const;
export type CreditStatus = (typeof CREDIT_STATUSES)[number];

/** What each status reads as in the picker; the member is what is stored (found in review, 2026-09-15). */
export const CREDIT_STATUS_LABELS: Readonly<Record<CreditStatus, string>> = {
  Good: t('credit.status.good'),
  Watch: t('credit.status.watch'),
  Hold: t('credit.status.hold'),
};

/**
 * The Settings key naming which exposure the limit is compared to, and its two
 * bases. 💰 The two give different answers on the same account, so any report of
 * who is over the limit states which basis it ran on.
 */
export const CREDIT_EXPOSURE_BASIS_KEY = 'credit.exposureBasis';
export const EXPOSURE_BASES = ['receivablesOnly', 'receivablesPlusOpenOrders'] as const;
export type ExposureBasis = (typeof EXPOSURE_BASES)[number];
/** The QuickBooks basis, and the seeded default. */
export const DEFAULT_EXPOSURE_BASIS: ExposureBasis = 'receivablesOnly';

/** 🔒 What each basis reads as in the credit warning (decision 3280): the sentence names it in words, never as the stored member. */
export const EXPOSURE_BASIS_LABELS: Readonly<Record<ExposureBasis, string>> = {
  receivablesOnly: t('credit.basis.receivablesOnly'),
  receivablesPlusOpenOrders: t('credit.basis.receivablesPlusOpenOrders'),
};

/** A blank override is never no limit and never zero: it resolves to the Organization row. */
export const DEFAULT_CREDIT_LIMIT_KEY = 'credit.defaultCreditLimit';

export interface ExposureInput {
  openReceivableCents: number;
  openSalesOrderCents: number;
}

export function exposureCents(input: ExposureInput, basis: ExposureBasis): number {
  return basis === 'receivablesOnly' ? input.openReceivableCents : input.openReceivableCents + input.openSalesOrderCents;
}

/** One line of an open Sales Order as the exposure reads it: its total with tax, its quantity, and how much of it issued invoices already bill. */
export interface OpenOrderLineFacts {
  totalCents: number;
  quantity: number;
  invoiced: number;
}

/**
 * 🔒 An open order's exposure is its uninvoiced remainder (piece 10; decision
 * 0964): each line counts the share of its total that no issued invoice bills
 * yet, so a half-invoiced order counts its other half once, and the invoiced
 * half is in receivables alone. A line billed in full or past it counts
 * nothing, and a line of negative quantity reads its share the same way.
 */
export function openOrderRemainderCents(lines: readonly OpenOrderLineFacts[]): number {
  return lines.reduce((sum, line) => {
    if (line.quantity === 0) return sum;
    const share = Math.min(1, Math.max(0, 1 - line.invoiced / line.quantity));
    return sum + roundHalfUpCents(line.totalCents * share);
  }, 0);
}

/** A null creditLimit? resolves to the Organization-wide default, never to no limit. */
export function resolvedLimitCents(companyLimit: Currency | null | undefined, organizationDefault: Currency): number {
  return companyLimit?.amount ?? organizationDefault.amount;
}

/**
 * The limit a screen shows, and whether it is the Organization's rather than
 * this account's. 🔨 A blank override reads as the default and says so; it is
 * never drawn as no credit and never as an empty row.
 */
export function resolvedCreditLimit(companyLimit: Currency | null | undefined, organizationDefault: Currency): { limit: Currency; isDefault: boolean } {
  return companyLimit ? { limit: companyLimit, isDefault: false } : { limit: organizationDefault, isDefault: true };
}

/**
 * The per-Company override a person typed, in integer cents, or null to fall
 * back to the Organization default. A fraction of a cent and a negative limit
 * are refused; zero is a real answer, an account we ship nothing to on credit.
 */
export function creditLimitOverrideProblem(value: unknown): string | null {
  if (value === null) return null;
  if (typeof value !== 'number' || !Number.isInteger(value)) return 'a credit limit is a whole amount of money, or blank for the organization default';
  if (value < 0) return 'a credit limit is not negative; leave it blank to use the organization default';
  return null;
}

export interface CreditStanding {
  status: CreditStatus;
  exposureCents: number;
  limitCents: number;
  basis: ExposureBasis;
  /** Over the limit warns and blocks nothing; the warning rides the approval the document already needs. */
  overLimit: boolean;
  /** Hold blocks new Sales Orders and new shipments — never safety or warranty work. */
  blocksNewOrders: boolean;
}

export function creditStanding(status: CreditStatus, exposure: ExposureInput, limitCents: number, basis: ExposureBasis): CreditStanding {
  const amount = exposureCents(exposure, basis);
  return { status, exposureCents: amount, limitCents, basis, overLimit: amount > limitCents, blocksNewOrders: status === 'Hold' };
}

/**
 * 🔒 The credit warning a Sales Order and the Quote's convert step carry
 * (decision 3280; T2269): the account over its limit, with the exposure, the
 * limit and the basis named, and an account on Credit Hold named as held. It
 * warns and blocks nothing; null where neither holds. The owner, 2026-10-11:
 * *"I wanted to throw a warning to him telling him that they're over their
 * credit limit so the salesperson knows to have a talk."*
 */
export function creditWarning(input: { companyName: string; standing: CreditStanding; currencyCode: string }): string | null {
  const { standing } = input;
  const held = standing.status === 'Hold';
  if (!standing.overLimit && !held) return null;
  const money = (amount: number) => formatCurrency({ amount, currencyCode: input.currencyCode });
  const parts: string[] = [];
  if (held) parts.push(fill(t('credit.warning.hold'), { company: input.companyName }));
  if (standing.overLimit) {
    parts.push(
      fill(t('credit.warning.over'), { who: held ? t('credit.warning.itToo') : input.companyName, exposure: money(standing.exposureCents), limit: money(standing.limitCents), basis: EXPOSURE_BASIS_LABELS[standing.basis] }),
    );
  }
  if (standing.status === 'Watch') parts.push(fill(t('credit.warning.status'), { status: CREDIT_STATUS_LABELS.Watch }));
  parts.push(t('credit.warning.talk'));
  return parts.join(' ');
}

/**
 * 🔨 Hold never blocks safety or warranty work — fire and life-safety work and
 * warranty work proceed regardless of credit status.
 */
export const WORK_EXEMPT_FROM_HOLD = ['fire', 'lifeSafety', 'warranty'] as const;
export type HoldExemptWork = (typeof WORK_EXEMPT_FROM_HOLD)[number];

export function holdBlocks(status: CreditStatus, work: { kind?: HoldExemptWork | null } = {}): boolean {
  if (status !== 'Hold') return false;
  return !(work.kind && (WORK_EXEMPT_FROM_HOLD as readonly string[]).includes(work.kind));
}

export class CreditStatusError extends Error {}

/**
 * 🔨 Only a person sets Hold. No exposure calculation, ageing sweep or import
 * writes it, so the writer names itself and anything but a person is refused.
 */
export type CreditWriter = 'person' | 'exposureCalculation' | 'ageingSweep' | 'import';

export function assertPersonSetHold(next: CreditStatus, writer: CreditWriter): void {
  if (next === 'Hold' && writer !== 'person') throw new CreditStatusError('nothing auto-holds an account; a person sets Hold');
}

/**
 * Reaching Hold notifies the account manager — the employment named by
 * assignedSalesRepEmploymentId? — and where that is null the notice falls to
 * Admin, the same place escalation off the top of the chain lands.
 */
export function holdNotice(assignedSalesRepEmploymentId: string | null): { toEmploymentId: string | null; toAdmin: boolean; endsReminderChain: true } {
  return { toEmploymentId: assignedSalesRepEmploymentId, toAdmin: assignedSalesRepEmploymentId === null, endsReminderChain: true };
}

/**
 * 🔨 requiresPoNumber composes with the Organization-wide threshold by union,
 * never replacement: a purchase order is required if either side says so, and a
 * bill-to SiteRecord overrides none of the customer's billing settings.
 */
export function purchaseOrderRequired(requiresPoNumber: boolean, documentTotalCents: number, thresholdCents: number): boolean {
  return requiresPoNumber || documentTotalCents > thresholdCents;
}
